import request from './axios';
import { 
  ElementInfo, 
  QueryElementParams, 
  CreateElementParams, 
  UpdateElementParams,
  CreateElementRelationParams,
  DeleteElementRelationParams,
  ElementSceneInfo
} from '../types/element.types';

/**
 * 获取场次元素列表
 * @param params 查询参数
 * @returns 场次元素列表
 */
export const getElementList = (params?: QueryElementParams) => {
  return request.get<ElementInfo[]>('/api/element', { params });
};

/**
 * 根据场次ID获取场次元素列表
 * @param sceneId 场次ID
 * @returns 场次元素列表
 */
export const getElementsBySceneId = (sceneId: string) => {
  return request.get<ElementInfo[]>(`/api/element/scene/${sceneId}`);
};

/**
 * 根据分场ID获取分场下所有场次元素列表
 * @param breakdownId 分场ID
 * @returns 场次元素列表
 */
export const getElementsByBreakdownId = (breakdownId: string) => {
  return request.get<ElementInfo[]>(`/api/element/breakdown/${breakdownId}`, { timeout: 30000 });
};

/**
 * 根据ID获取单个场次元素
 * @param elementId 场次元素ID
 * @returns 场次元素信息
 */
export const getElementById = (elementId: string) => {
  return request.get<ElementInfo>(`/api/element/${elementId}`);
};

/**
 * 创建场次元素
 * @param data 创建场次元素参数
 * @returns 创建的场次元素信息
 */
export const createElement = (data: CreateElementParams) => {
  return request.post<ElementInfo>('/api/element', data);
};

/**
 * 批量创建场次元素
 * @param data 批量创建场次元素参数
 * @returns 创建的场次元素信息列表
 */
export const createElementBatch = (data: { elements: CreateElementParams[] }) => {
  return request.post<ElementInfo[]>('/api/element/batch', data);
};

/**
 * 更新场次元素
 * @param elementId 场次元素ID
 * @param data 更新场次元素参数
 * @returns 更新后的场次元素信息
 */
export const updateElement = (elementId: string, data: UpdateElementParams) => {
  return request.patch<ElementInfo>(`/api/element/${elementId}`, data);
};

/**
 * 批量更新场次元素
 * 单次请求更新多个场次元素，提升查找替换等场景的性能
 * @param updates 更新列表 { elementID, data }
 * @returns 更新后的场次元素列表
 */
export const batchUpdateElements = (
  updates: Array<{ elementID: string; data: UpdateElementParams }>
) => {
  return request.patch<ElementInfo[]>('/api/element/batch', { updates });
};

/**
 * 删除场次元素
 * @param elementId 场次元素ID
 */
export const deleteElement = (elementId: string) => {
  return request.delete(`/api/element/${elementId}`);
};

/**
 * 创建场次元素关系
 * 将已存在的场次元素关联到场次
 * @param data 创建关系参数
 * @returns 创建关系的结果
 */
export const createElementRelation = (data: CreateElementRelationParams) => {
  // 调试：打印请求参数
  console.log('[DEBUG] createElementRelation 请求参数:', JSON.stringify(data, null, 2));
  console.log('[DEBUG] createElementRelation 参数类型检查:', {
    elementID: { value: data.elementID, type: typeof data.elementID },
    sceneID: { value: data.sceneID, type: typeof data.sceneID },
    elementQtyInScene: { value: data.elementQtyInScene, type: typeof data.elementQtyInScene },
    elementBelongBreakdownID: { value: data.elementBelongBreakdownID, type: typeof data.elementBelongBreakdownID },
  });
  return request.post('/api/element/relations', data);
};

/**
 * 批量创建场次元素关系
 * 将多个已存在的场次元素关联到场次
 * @param relations 批量创建关系参数数组
 * @returns 批量创建关系的结果
 */
export const batchCreateElementRelations = (relations: CreateElementRelationParams[]) => {
  return request.post('/api/element/relations/batch', { relations });
};

/**
 * 删除场次元素关系
 * 移除场次元素与场次的关联关系
 * @param data 删除关系参数
 * @returns 删除关系的结果
 */
export const deleteElementRelation = (data: DeleteElementRelationParams) => {
  return request.delete(`/api/element/relations/scene/${data.elementID}/${data.sceneID}`);
};

/**
 * 批量删除场次元素关系
 * 批量移除场次元素与场次的关联关系
 * @param relations 批量删除关系参数数组
 * @returns 批量删除关系的结果
 */
export const batchDeleteElementRelations = (relations: DeleteElementRelationParams[]) => {
  return request.delete('/api/element/relations/batch', { data: { relations } });
};

/**
 * 更新场次元素与场次的关系
 * @param elementId 场次元素ID
 * @param sceneId 场次ID
 * @param data 更新关系参数（包含 elementQtyInScene 等）
 * @returns 更新后的关系信息
 */
export const updateElementRelation = (
  elementId: string,
  sceneId: string,
  data: { elementQtyInScene?: number }
) => {
  return request.patch(`/api/element/relations/scene/${elementId}/${sceneId}`, {
    elementID: elementId,
    sceneID: sceneId,
    elementQtyInScene: data.elementQtyInScene
  });
};

/**
 * 根据分场ID获取场次元素关系列表
 * @param breakdownId 分场ID
 * @returns 场次元素关系列表
 */
export const getElementRelationsByBreakdownID = (breakdownId: string) => {
  return request.get<ElementSceneInfo[]>(`/api/element/relations/breakdown/${breakdownId}`, {
    timeout: 30000,
  });
};
